import {
  IFtthOrchestrator,
  IModemReturnOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { IAccess, IModem } from "@nju/data-access-portal-product";
import { getPaymentErrorCategoryMessage } from "@nju/feature-portal-payment";
import { IconName } from "@nju/icons";
import { differenceInCalendarDays, formatDate, formatPrice } from "@nju/utils";
import { IServiceCTABannerConditions } from "./types";

interface IStatusProps {
  title: string;
  text: string;
  iconName: IconName;
}

interface IGetStatusProps {
  access: IAccess | undefined;
  anniversaryDate: string | undefined;
  conditions: IServiceCTABannerConditions;
  isLoyaltyOffer: boolean;
  modemProducts: IModem | undefined;
  modemReturnOrchestrator: IModemReturnOrchestrator | undefined;
  orchestrator: IFtthOrchestrator | undefined;
  penaltyFee: number | undefined;
  willBreakLoyaltyOnResignation: boolean;
}

export function getFtthStatusData({
  conditions,
  anniversaryDate,
  access,
  isLoyaltyOffer,
  modemProducts,
  modemReturnOrchestrator,
  willBreakLoyaltyOnResignation,
  penaltyFee,
  orchestrator,
}: IGetStatusProps): IStatusProps | undefined {
  const {
    isTimeForPaymentWithoutProducts,
    isTimeForPaymentWithProducts,
    isPaymentInProgress,
    isPaymentError,
    isDWPaymentPending,
    isBMPaymentPending,
    isUnsubscribed,
    isNoPayment,
    isAlmostTerminated,
    isDisconnected,
    isSuspended,
    isPendingReactivation,
    isPendingTermination,
    isTermination,
    isPending,
    haveToPaidBeforeOrder,
    isOrderProcessAvailable,
    isUnreturnedModem,
  } = conditions;

  if (isBMPaymentPending) {
    return {
      title: "płatność w trakcie realizacji",
      text: "Wróć do płatności online, aby dokończyć rozpoczęty proces lub poczekaj na jego realizację. Możesz też wybrać inną metodę.",
      iconName: "spinner",
    };
  }

  // sample user to invoke this error: portal251+p7@rafal.im
  // https://dop-orange.atlassian.net/wiki/spaces/DGB/pages/1367703621/Mock
  if (isPaymentError && orchestrator?.paymentErrorCategory) {
    return {
      title: "błąd płatności",
      text: getPaymentErrorCategoryMessage(orchestrator?.paymentErrorCategory),
      iconName: "exclamation-circle",
    };
  }

  if (isDWPaymentPending) {
    return {
      title: "właśnie realizujemy płatność",
      text: "Daj nam chwilę lub kliknij odśwież. Możesz też wybrać inną metodę płatności",
      iconName: "spinner",
    };
  }

  if (isPaymentInProgress) {
    return {
      title: "właśnie realizujemy płatność",
      text: "Daj nam chwilę lub kliknij odśwież.",
      iconName: "spinner",
    };
  }

  if (isTimeForPaymentWithoutProducts) {
    return {
      title: "pora na płatność",
      text: "Zapłać, by móc korzystać z internetu po instalacji.",
      iconName: "money-bill-wave",
    };
  }

  if (
    isTimeForPaymentWithProducts &&
    orchestrator?.paymentStartDate &&
    orchestrator?.orderSummary?.totalOrderPrice
  ) {
    const time = formatDate(orchestrator.paymentStartDate, false);

    return {
      title: "pora na płatność",
      text: `${time} zainstalowaliśmy usługę nju światłowód w Twoim lokalu. Zapłać ${orchestrator.orderSummary.totalOrderPrice} zł i pamiętaj, że do momentu zapłaty, usługa będzie zablokowana. Na wpłatę masz 30 dni od momentu instalacji usługi, inaczej umowa wygaśnie.`,
      iconName: "money-bill-wave",
    };
  }

  if (isUnsubscribed && anniversaryDate) {
    const time = formatDate(anniversaryDate, false);
    return {
      title: "zaplanowane zawieszenie",
      text: `Usługa nju światłowód zostanie zawieszona ${time}. W każdej chwili możesz anulować zawieszenie usługi.`,
      iconName: "exclamation-circle",
    };
  }

  if (isNoPayment && access?.statusChangeDate) {
    const time = formatDate(access.statusChangeDate);
    return {
      title: "brak płatności za usługę",
      text: `${time} nie udało nam się pobrać płatności. Jeśli w ciągu 24 godzin nie otrzymamy płatności, Twoja usługa zostanie wyłączona.`,
      iconName: "shopping-bag",
    };
  }

  if (
    isAlmostTerminated &&
    isLoyaltyOffer &&
    willBreakLoyaltyOnResignation &&
    access?.statusChangeDate &&
    penaltyFee
  ) {
    const statusChangedDate = formatDate(access?.statusChangeDate, false);
    return {
      title: "usługa została wyłączona",
      text: `${statusChangedDate} usługa nju światłowód została wyłączona ze względu na brak płatności. Możesz ją ponownie włączyć w ciągu 31 dni od dnia wyłączenia. Jeśli nie opłacisz usługi, nie spełnisz warunku wykupienia 24 opłat miesięcznych. W związku z tym wyrównamy Twoją opłatę za instalację usługi do ceny bez promocji, czyli doliczymy ${formatPrice(
        penaltyFee,
      )} zł. Zapewnij środki na koncie.`,
      iconName: "exclamation-circle",
    };
  }

  if (isAlmostTerminated && access?.terminationDate) {
    const terminationDate = formatDate(access?.terminationDate, false);
    return {
      title: "usługa została wyłączona",
      text: `Usługa nju światłowód jest nieaktywna. Jeśli nadal chcesz z niej korzystać, możesz ją ponownie włączyć do ${terminationDate}.`,
      iconName: "exclamation-circle",
    };
  }

  if (isDisconnected && access?.statusChangeDate) {
    const time = formatDate(access?.statusChangeDate, false);
    return {
      title: "usługa została zawieszona",
      text: `${time} usługa nju światłowód została zawieszona. Możesz ją przywrócić w dowolnym momencie przez 93 dni od dnia zawieszenia usługi.`,
      iconName: "exclamation-circle",
    };
  }

  if (
    isSuspended &&
    isLoyaltyOffer &&
    willBreakLoyaltyOnResignation &&
    access?.statusChangeDate &&
    penaltyFee
  ) {
    const statusChangedDate = formatDate(access?.statusChangeDate, false);
    return {
      title: "usługa została wyłączona",
      text: `${statusChangedDate} usługa nju światłowód została wyłączona ze względu na brak płatności. Możesz ją ponownie włączyć w ciągu 31 dni od dnia wyłączenia. Jeśli nie opłacisz usługi, nie spełnisz warunku wykupienia 24 opłat miesięcznych. W związku z tym wyrównamy Twoją opłatę za instalację usługi do ceny bez promocji, czyli doliczymy ${formatPrice(
        penaltyFee,
      )} zł. Zapewnij środki na koncie.`,
      iconName: "exclamation-circle",
    };
  }

  if (isSuspended && access?.terminationDate) {
    const terminationDate = formatDate(access?.terminationDate, false);

    return {
      title: "usługa została wyłączona",
      text: `Usługa nju światłowód jest nieaktywna. Jeśli nadal chcesz z niej korzystać, możesz ją ponownie włączyć do ${terminationDate}.`,
      iconName: "exclamation-circle",
    };
  }

  if (isSuspended && access?.statusChangeDate) {
    const statusChangedDate = formatDate(access?.statusChangeDate, false);

    return {
      title: "usługa została wyłączona",
      text: `${statusChangedDate} usługa nju światłowód została wyłączona ze względu na brak płatności. Możesz ją ponownie włączyć w ciągu 31 dni od dnia wyłączenia.`,
      iconName: "exclamation-circle",
    };
  }

  if (isPendingReactivation) {
    return {
      title: "usługa w trakcie włączania",
      text: `Trwa ponowna aktywacja Twojej usługi.`,
      iconName: "spinner",
    };
  }

  if (
    isPendingTermination &&
    access?.anniversaryDate &&
    modemProducts?.characteristic?.serialNumber
  ) {
    const time = formatDate(access?.anniversaryDate, false);
    const diffDays = differenceInCalendarDays(access?.anniversaryDate);
    return {
      title: `usługa zostanie zakończona za ${diffDays} dni (${time})`,
      text: `Twoja usługa nju światłowód będzie aktywna do ${time}. Pamiętaj o zwrocie modemu nr ${modemProducts?.characteristic?.serialNumber} w ciągu 14 dni kalendarzowych od dnia zakończenia usługi.`,
      iconName: "exclamation-circle",
    };
  }

  if (isUnreturnedModem && modemReturnOrchestrator?.contractPenaltyFee) {
    return {
      title: "zwrot modemu",
      text: `Nie otrzymaliśmy modemu, dlatego wystawiliśmy notę obciążeniową na kwotę ${modemReturnOrchestrator?.contractPenaltyFee} zł. Prosimy o zapewnienie środków na koncie lub zwrócenie modemu. Jeśli modem został już zwrócony, zignoruj ten komunikat.`,
      iconName: "exclamation-circle",
    };
  }

  if (haveToPaidBeforeOrder) {
    return {
      title: "nie odnotowaliśmy płatności",
      text: "Masz nieuregulowane opłaty. Sprawdź szczegóły i wybierz metodę płatności, by uregulować zadłużenie.",
      iconName: "exclamation-circle",
    };
  }

  if (
    isTermination &&
    modemReturnOrchestrator?.modemSerialNumber &&
    modemReturnOrchestrator?.caseContext === "14CANCELLATION"
  ) {
    return {
      title: "w trakcie odstąpienia",
      text: `Otrzymaliśmy Twoje odstąpienie od umowy dot. usługi nju światłowód. Pamiętaj o zwrocie modemu nr ${modemReturnOrchestrator?.modemSerialNumber} w ciągu 14 dni kalendarzowych od dnia odstąpienia.`,
      iconName: "exclamation-circle",
    };
  }

  if (
    isTermination &&
    modemReturnOrchestrator?.modemSerialNumber &&
    modemReturnOrchestrator?.caseContext === "TERMINATION" &&
    modemReturnOrchestrator?.contractTerminationDate
  ) {
    const terminationDate = formatDate(
      modemReturnOrchestrator?.contractTerminationDate,
      false,
    );
    return {
      title: "umowa zakończona",
      text: `Umowa została zakończona ${terminationDate}. Pamiętaj by zwrócić modem nr ${modemReturnOrchestrator?.modemSerialNumber} w ciągu 14 dni kalendarzowych od dnia rozwiązania umowy.`,
      iconName: "exclamation-circle",
    };
  }

  if (isOrderProcessAvailable) {
    return {
      title: "czas na światłowód",
      text: "Sprawdź dostępność usługi pod wybranym adresem,  a następnie wybierz termin instalacji usługi.",
      iconName: "shopping-bag",
    };
  }

  // general CTA banner displayed while we are in pending state.
  // the only CTA banner used in both - service and onboarding process
  if (isPending) {
    return {
      title: "realizacja w toku",
      text: "Potrzebujemy jeszcze chwili na realizację po naszej stronie.",
      iconName: "spinner",
    };
  }

  return undefined;
}
